import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { I18n } from 'core';
import { TeledetectionData } from 'types/models';
import teledetectionService from 'services/remote/TeledetectionService';
import TeledetectionChart from 'components/TeledetectionChart/TeledetectionChart';
import {
  isGenerationAvailableForTab,
  shouldExpandNetworkCard,
  shouldShowGeneration,
  shouldShowSelfConsumption,
} from 'components/TeledetectionChart/generationAvailability';
import { toast } from 'react-toastify';
import wattsConverterService from 'services/local/WattsConverter';
import { ReactComponent as NetworkIcon } from 'assets/icons/Network.svg';
import { ReactComponent as GenerationIcon } from 'assets/icons/Generation.svg';
import { ReactComponent as ShrinkIcon } from 'assets/icons/Shrink.svg';
import { ReactComponent as SelfConsumptionIcon } from 'assets/icons/SelfConsumption.svg';
import cx from 'classnames';

import { clientServiceInstance, cupsServiceInstance } from 'services';
import { Colors } from 'styles';
import FullPageSyncLoader from 'components/FullPageSyncLoader';
import { DayPicker, MonthPicker, YearPicker } from 'components/Input';
import { useDispatch, useSelector } from 'react-redux';
import { GlobalStoreState } from 'types/state';
import { navigate } from 'navigation';
import {
  fetchClientCUPSListSuccessAdmin,
  fetchCUPSTestSuccess,
  updateClientCUPSadmin,
} from 'redux/actions/ClientCupsActions';

import styles from './ChartsPage.module.scss';

// --- Constants outside component ---

const NAV_YEAR = 'year';
const NAV_MONTH = 'month';
const NAV_DAY = 'day';
const ICON_SIZE = '25px';

const getInitialDate = (): Date => {
  const yesterday = new Date();
  yesterday.setDate(yesterday.getDate() - 1);
  return yesterday;
};

const INITIAL_DATE = getInitialDate();

// --- DataItem ---

interface DataItemPropsI {
  icon: JSX.Element;
  name: string;
  data: number;
}

const DataItem = ({ icon, name, data }: DataItemPropsI): JSX.Element => {
  const converted = wattsConverterService.convertWatts(data);
  return (
    <div className={cx(styles.DataItem)}>
      <div className={styles.type}>
        {icon}
        <p className={styles.name}>{I18n.t(name)}</p>
      </div>
      <div className={styles.value}>
        <p className={styles.data}>{converted.num}</p>
        <p className={styles.unit}>{converted.unitHour}</p>
      </div>
    </div>
  );
};

// --- Helpers ---

const findLastNonZeroData = (data: { y: number; x: number }[]): number =>
  data.reduce((lastNonZero, current) => (current.y !== 0 ? Math.max(lastNonZero, current.x) : lastNonZero), 0);

// --- Main Component ---

const ChartsPageAdmin = (props: any): JSX.Element => {
  const {
    match: {
      params: { cupsID },
    },
  } = props;

  const dispatch = useDispatch();
  const cupsSelected = useSelector((state: GlobalStoreState) => state.clientCUPS.selectedCups);
  const selectedMulticau = useSelector((state: GlobalStoreState) => state.clientCUPS.selectedMulticau);

  const [teledetectionData, setTeledetectionData] = useState(new TeledetectionData());
  const [date, setDate] = useState({
    day: INITIAL_DATE.getDate(),
    month: INITIAL_DATE.getMonth() + 1,
    year: INITIAL_DATE.getFullYear(),
  });
  const [currentTab, setCurrentTab] = useState(NAV_DAY);
  const [dataIsLoad, setDataIsLoad] = useState(false);
  const [loadingDetailData, setLoadingDetailData] = useState(false);
  const lastFetchRef = useRef('');
  const generationAvailable = isGenerationAvailableForTab(
    currentTab,
    teledetectionData.generationAvailable,
    teledetectionData.hasHistoricalGenerationGap,
  );
  const showGeneration = shouldShowGeneration(cupsSelected?.fotovoltaica || 'no', generationAvailable);
  const showSelfConsumption = shouldShowSelfConsumption(
    cupsSelected?.fotovoltaica || 'no',
    generationAvailable,
  );

  // Init: cargar CUPS data
  useEffect(() => {
    clientServiceInstance.getClient(cupsID).then((response) => {
      response.setAttr('cups', cupsID);
      dispatch(fetchCUPSTestSuccess(response));
      if (response?.nif !== '') {
        cupsServiceInstance.getCupsFromUserId(response?.nif).then((cupsList) => {
          if (cupsList.length > 0) {
            const cupsById = cupsList.find((item) => item.cups === cupsID);
            dispatch(updateClientCUPSadmin(cupsById || cupsList[0]));
            dispatch(fetchClientCUPSListSuccessAdmin(cupsList));
          }
        });
      } else {
        setDataIsLoad(true);
      }
    });
  }, [cupsID]);

  // Redirigir si CUPS no coincide con URL
  useEffect(() => {
    if (cupsSelected && cupsSelected.cups !== cupsID) {
      navigate(`/tecnic/cups/${cupsID}/graficos`);
    }
  }, [cupsSelected]);

  // Buscar última fecha con datos
  useEffect(() => {
    const codiClient = selectedMulticau?.codiClient || undefined;

    const fetchLastDateWithData = async () => {
      try {
        const yearData = await teledetectionService.getTeledetectionData(
          'year', cupsID, date.year, undefined, undefined, codiClient,
        );

        const lastMonthWithData = Math.max(
          findLastNonZeroData(yearData.generationData),
          findLastNonZeroData(yearData.selfConsumptionData),
          findLastNonZeroData(yearData.shirnkData),
          findLastNonZeroData(yearData.networkonData),
        );

        const monthData = await teledetectionService.getTeledetectionData(
          'month', cupsID, date.year, lastMonthWithData, undefined, codiClient,
        );

        const lastDayWithData = Math.max(
          findLastNonZeroData(monthData.generationData),
          findLastNonZeroData(monthData.selfConsumptionData),
          findLastNonZeroData(monthData.shirnkData),
          findLastNonZeroData(monthData.networkonData),
        );

        setDate({
          day: lastDayWithData,
          month: lastMonthWithData,
          year: date.year,
        });
      } catch {
        toast.error('Error getting teledetection data');
      }
    };

    fetchLastDateWithData();
  }, [cupsID]);

  // Fetch teledetection data
  useEffect(() => {
    if (!cupsSelected) return;

    const codiClient = selectedMulticau?.codiClient || undefined;
    const fetchKey = `${currentTab}_${cupsSelected.cups}_${date.year}_${date.month}_${date.day}_${codiClient ?? ''}`;
    if (lastFetchRef.current === fetchKey) return;
    lastFetchRef.current = fetchKey;

    const fetchData = async () => {
      setLoadingDetailData(true);
      try {
        const teledetectionObj = await teledetectionService.getTeledetectionData(
          currentTab, cupsSelected.cups, date.year, date.month, date.day, codiClient,
        );
        if (teledetectionObj.generationData !== undefined) {
          setTeledetectionData(teledetectionObj);
        }
        setDataIsLoad(true);
      } catch {
        toast.error('errorGettingTeledetectionData');
      } finally {
        setLoadingDetailData(false);
      }
    };

    fetchData();
  }, [currentTab, date, cupsSelected, selectedMulticau]);

  const updateDate = useCallback((newDate: Date): void => {
    if (!newDate) return;
    setDate({
      day: newDate.getDate(),
      month: newDate.getMonth() + 1,
      year: newDate.getFullYear(),
    });
  }, []);

  const pickerDate = useMemo(
    () =>
      Number.isNaN(date.year)
        ? new Date(`${INITIAL_DATE.getMonth() + 1}/${INITIAL_DATE.getDate()}/${INITIAL_DATE.getFullYear()}`)
        : new Date(`${date.month}/${date.day}/${date.year}`),
    [date],
  );

  const dateComponent = useMemo(() => {
    if (currentTab === NAV_DAY) {
      return <DayPicker date={pickerDate} onChangeDate={updateDate} />;
    }
    if (currentTab === NAV_MONTH) {
      return <MonthPicker anchorSelector="right" value={pickerDate} onChange={updateDate} />;
    }
    if (currentTab === NAV_YEAR) {
      return <YearPicker anchorSelector="right" value={pickerDate} onChange={updateDate} />;
    }
    return <></>;
  }, [currentTab, pickerDate, updateDate]);

  if (!dataIsLoad) {
    return <FullPageSyncLoader icon />;
  }

  return (
    <div className={styles.root}>
      {cupsSelected !== null ? (
        <div className={styles.chartWrapper}>
          <TeledetectionChart
            isLoading={loadingDetailData}
            fotovoltaica={cupsSelected?.fotovoltaica || 'no'}
            teledetectionData={teledetectionData}
            dateComponent={dateComponent}
            currentTab={currentTab}
            setCurrentTabFunction={setCurrentTab}
          />
          {!loadingDetailData ? (
            <div className={styles.grid_container}>
              {cupsSelected?.fotovoltaica === 'si' ? (
                <>
                  {showGeneration && teledetectionData.generationData !== undefined ? (
                    <div className={styles.item}>
                      <DataItem
                        icon={<GenerationIcon fill={Colors.COLOR_GENERACIO} width={ICON_SIZE} height={ICON_SIZE} />}
                        name="generation"
                        data={teledetectionData.totals.generation}
                      />
                    </div>
                  ) : null}
                  <div className={styles.item}>
                    {teledetectionData.shirnkData !== undefined ? (
                      <DataItem
                        icon={<ShrinkIcon fill={Colors.COLOR_BLUE_200} width={ICON_SIZE} height={ICON_SIZE} />}
                        name="shrinking"
                        data={teledetectionData.totals.shrink}
                      />
                    ) : null}
                  </div>
                  {showSelfConsumption && teledetectionData.selfConsumptionData !== undefined ? (
                    <div className={styles.item}>
                      <DataItem
                        icon={<SelfConsumptionIcon fill={Colors.COLOR_ELECSUMGREEN} width={ICON_SIZE} height={ICON_SIZE} />}
                        name="selfConsumption"
                        data={teledetectionData.totals.selfconsumption}
                      />
                    </div>
                  ) : null}
                </>
              ) : null}
              <div
                className={cx(styles.item, {
                  [styles.expand]: shouldExpandNetworkCard(cupsSelected?.fotovoltaica || 'no'),
                })}
              >
                {teledetectionData.networkonData !== undefined && teledetectionData.totals !== undefined ? (
                  <DataItem
                    icon={<NetworkIcon fill={Colors.COLOR_GRAY_500} width={ICON_SIZE} height={ICON_SIZE} />}
                    name="network"
                    data={teledetectionData.totals.network}
                  />
                ) : null}
              </div>
            </div>
          ) : (
            <div style={{ height: 140 }} />
          )}
        </div>
      ) : (
        <div style={{ height: 140 }}>{I18n.t(`DashboardPage.nodata_grafic`)}</div>
      )}
    </div>
  );
};

export default ChartsPageAdmin;
